import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { toast } from "@/lib/toast";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
  head: () => ({ meta: [{ title: "نسيت كلمة المرور — Lecmo" }] }),
});

function ForgotPasswordPage() {
  const { locale, dir } = useLocale();
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const t = (en: string, ar: string, fr: string) =>
    locale === "ar" ? ar : locale === "fr" ? fr : en;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
      toast.success(
        t("Check your email for a reset link.", "تحقّق من بريدك لرابط إعادة التعيين.", "Vérifiez votre email."),
      );
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteShell>
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-24" dir={dir}>
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="t-h3 tracking-[var(--tracking-wider)]">LECMO</span>
            <span className="t-eyebrow tracking-[var(--tracking-wider)]">perfume</span>
          </span>
        </Link>
        <h1 className="mt-10 text-center t-h2 text-foreground">
          {t("Forgot password", "نسيت كلمة المرور", "Mot de passe oublié")}
        </h1>
        <p className="mt-2 text-center t-caption uppercase tracking-[var(--tracking-wider)] text-muted-foreground">
          {t(
            "We will send you a reset link",
            "سنرسل لك رابط إعادة التعيين",
            "Nous vous enverrons un lien",
          )}
        </p>

        {sent ? (
          <div className="mt-10 border border-border bg-secondary/40 p-6 text-center">
            <p className="t-body-sm text-foreground">
              {t(
                "If an account exists for this email, a reset link has been sent.",
                "إذا كان هناك حساب بهذا البريد، تمّ إرسال رابط إعادة التعيين.",
                "Si un compte existe pour cet email, un lien a été envoyé.",
              )}
            </p>
            <Link
              to="/auth"
              search={{ redirect: "/account", mode: "signin" }}
              className="mt-6 inline-block t-caption uppercase tracking-[var(--tracking-wider)] text-primary underline"
            >
              {t("Back to sign in", "العودة لتسجيل الدخول", "Retour à la connexion")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 space-y-3">
            <Input
              type="email"
              required
              placeholder={t("Email", "البريد الإلكتروني", "Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "..." : t("Send reset link", "إرسال الرابط", "Envoyer le lien")}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link
            to="/auth"
            search={{ redirect: "/account", mode: "signin" }}
            className="t-link"
          >
            {t("Back to sign in", "العودة لتسجيل الدخول", "Retour à la connexion")}
          </Link>
        </p>
      </div>
    </div>
    </SiteShell>
  );
}
